import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";
import { cn } from "@gettactic/helpers";
import { createPortal } from "react-dom";

type Ctx = {
  asToggleTip: boolean;
  open: boolean;
  setOpen: (v: boolean) => void;
};
const TooltipCtx = React.createContext<Ctx | null>(null);

const TooltipProvider = TooltipPrimitive.Provider;

type TooltipRootProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Root
> & {
  /** When true, behave like a tappable toggletip (mobile-friendly). */
  asToggleTip?: boolean;
};

/**
 * A tooltip root. With `asToggleTip` it opens and closes on tap instead of hover, for touch
 * screens.
 */
const Tooltip = ({
  asToggleTip = false,
  open: openProp,
  defaultOpen,
  onOpenChange,
  delayDuration,
  ...rest
}: TooltipRootProps) => {
  const [internalOpen, setInternalOpen] = React.useState<boolean>(
    !!defaultOpen
  );
  const isControlled = openProp !== undefined;
  const open = isControlled ? (openProp as boolean) : internalOpen;

  // In toggletip mode we control open state ourselves and ignore hover/focus changes
  const handleOpenChange = (next: boolean) => {
    if (asToggleTip) return; // ignore hover/focus driven changes
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  return (
    <TooltipCtx.Provider
      value={{
        asToggleTip,
        open,
        setOpen: (v) => {
          if (!isControlled) setInternalOpen(v);
          onOpenChange?.(v);
        }
      }}
    >
      <TooltipPrimitive.Root
        open={asToggleTip ? open : openProp}
        defaultOpen={asToggleTip ? undefined : defaultOpen}
        onOpenChange={handleOpenChange}
        delayDuration={asToggleTip ? 0 : delayDuration}
        disableHoverableContent={asToggleTip || rest.disableHoverableContent}
        {...rest}
      />
    </TooltipCtx.Provider>
  );
};

type TriggerProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Trigger
> & {
  /** You can also pass here to force toggletip just for this trigger (optional). */
  asToggleTip?: boolean;
};

const TooltipTrigger = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Trigger>,
  TriggerProps
>(({ asToggleTip, onClick, ...props }, ref) => {
  const ctx = React.useContext(TooltipCtx);
  const toggletip = asToggleTip ?? ctx?.asToggleTip ?? false;

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    if (toggletip && ctx) {
      e.preventDefault();
      e.stopPropagation();
      ctx.setOpen(!ctx.open);
    }
    onClick?.(e);
  };

  // Add a11y hints when acting like a button/toggletip
  const a11y = toggletip
    ? {
        "aria-expanded": ctx?.open ?? false,
        "aria-haspopup": "dialog"
      }
    : {};

  return (
    // @ts-ignore
    <TooltipPrimitive.Trigger
      ref={ref}
      onClick={handleClick}
      {...a11y}
      {...props}
    />
  );
});
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

const TooltipPortal = TooltipPrimitive.Portal;

type ContentProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Content
> & {
  closeOnPointerDownOutside?: boolean;
};
/** The tooltip bubble; in toggletip mode Escape and a tap outside close it. */
const TooltipContent = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  ContentProps
>(
  (
    {
      className,
      sideOffset = 4,
      onPointerDownOutside,
      onEscapeKeyDown,
      closeOnPointerDownOutside = true,
      ...props
    },
    ref
  ) => {
    const ctx = React.useContext(TooltipCtx);

    // In toggletip mode, close on outside press and ESC
    const handlePointerDownOutside: typeof onPointerDownOutside = (e) => {
      if (ctx?.asToggleTip && closeOnPointerDownOutside) ctx.setOpen(false);
      onPointerDownOutside?.(e);
    };
    const handleEscape: typeof onEscapeKeyDown = (e) => {
      if (ctx?.asToggleTip) ctx.setOpen(false);
      onEscapeKeyDown?.(e);
    };

    // Also close on window resize/scroll in toggletip mode (mobile UX nicety)
    React.useEffect(() => {
      if (!ctx?.asToggleTip) return;
      const close = () => ctx.setOpen(false);
      window.addEventListener("resize", close, { passive: true });
      window.addEventListener("orientationchange", close, { passive: true });
      return () => {
        window.removeEventListener("resize", close);
        window.removeEventListener("orientationchange", close);
      };
    }, [ctx]);

    return createPortal(
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        onPointerDownOutside={handlePointerDownOutside}
        onEscapeKeyDown={handleEscape}
        className={cn(
          "fade-in-0 zoom-in-95 data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-tooltip animate-in overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-popover-foreground text-sm shadow-md data-[state=closed]:animate-out",
          // Make it a bit larger/tappable on mobile
          ctx?.asToggleTip ? "touch-manipulation select-none" : "",
          className
        )}
        {...props}
      />,
      window.document.body
    );
  }
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipPortal
};
